/**
 * AuthActionPage — handles Firebase Auth email action links.
 *
 * Firebase sends links to talismantravel.app/auth-action?mode=...&oobCode=...
 * This page intercepts those, calls the correct Firebase SDK method, and shows
 * a branded Talisman UI instead of the generic Firebase-hosted page.
 *
 * Modes handled:
 *   resetPassword  — verify the code, show a new-password form, confirm reset
 *   verifyEmail    — apply the code automatically, show success
 *   recoverEmail   — apply the code, show success
 */

import { useState, useEffect, useRef } from 'react';
import {
  getAuth,
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
} from 'firebase/auth';
import { TalismanLogo } from './Icons';
import { CardTintStreaks, FIELD_AUTH, AUTH_ERROR, fieldBorder } from './UI';

type Mode = 'resetPassword' | 'verifyEmail' | 'recoverEmail' | 'unknown';
type Stage = 'loading' | 'form' | 'success' | 'error';

function actionLinkError(error: unknown): string {
  const code = (error as { code?: string })?.code;
  if (code === 'auth/network-request-failed') return 'Check your connection and reopen this email link.';
  if (code === 'auth/too-many-requests') return 'Too many attempts. Please wait before trying again.';
  if (code === 'auth/expired-action-code') return 'This link has expired. Return to Talisman to request a new one.';
  if (code === 'auth/invalid-action-code') return 'This link is invalid or has already been used. If you verified your email, return to Talisman and tap “I’ve verified it”. Otherwise, request a new link.';
  return 'We could not check this link. Please try again later.';
}

export function AuthActionPage() {
  const params  = new URLSearchParams(window.location.search);
  const mode    = (params.get('mode') ?? 'unknown') as Mode;
  const oobCode = params.get('oobCode') ?? '';

  const [stage,    setStage]    = useState<Stage>('loading');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [error,    setError]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Reuse the one-use request during effect replay; never apply a code twice.
  const actionAttempt = useRef<{ key: string; result: Promise<string | void> } | null>(null);

  const authInstance = getAuth();

  /* ── Set page title ────────────────────────────────────────────────────── */
  useEffect(() => {
    const titles: Record<Mode, string> = {
      resetPassword: 'Talisman — Reset your password',
      verifyEmail:   'Talisman — Verify your email',
      recoverEmail:  'Talisman — Recover your email',
      unknown:       'Talisman',
    };
    document.title = titles[mode];
  }, [mode]);

  /* ── Initialise: verify code or apply action code automatically ─────────── */
  useEffect(() => {
    if (!oobCode) { setStage('error'); setError('This link is missing its verification code. Open the full link from your email.'); return; }
    if (!['resetPassword', 'verifyEmail', 'recoverEmail'].includes(mode)) {
      setStage('error');
      setError('Unrecognised action. Please return to Talisman and try again.');
      return;
    }
    let active = true;
    const key = `${mode}:${oobCode}`;
    if (actionAttempt.current?.key !== key) {
      actionAttempt.current = {
        key,
        result: mode === 'resetPassword'
          ? verifyPasswordResetCode(authInstance, oobCode)
          : applyActionCode(authInstance, oobCode),
      };
    }
    actionAttempt.current.result.then(emailAddr => {
      if (!active) return;
      if (mode === 'resetPassword') {
        setEmail(emailAddr as string);
        setStage('form');
      } else setStage('success');
    }).catch(error => {
      if (!active) return;
      setStage('error');
      setError(actionLinkError(error));
    });
    return () => { active = false; };
  }, [authInstance, mode, oobCode]);

  /* ── Submit new password ──────────────────────────────────────────────── */
  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 6)  { setError('Password must be at least 6 characters.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await confirmPasswordReset(authInstance, oobCode, password);
      setStage('success');
    } catch {
      setError('Could not reset your password. The link may have expired — please request a new one.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Helpers ──────────────────────────────────────────────────────────── */
  const successMessages: Partial<Record<Mode, { heading: string; body: string }>> = {
    resetPassword: { heading: 'Password updated', body: 'You can now sign in with your new password.' },
    verifyEmail:   { heading: 'Email verified',   body: 'Your email address has been confirmed. Open Talisman to continue.' },
    recoverEmail:  { heading: 'Email recovered',  body: 'Your sign-in email has been restored. Open Talisman to continue.' },
  };

  /* ── Render ───────────────────────────────────────────────────────────── */
  return (
    <div className="auth-entry brand-loading-surface min-h-screen flex flex-col items-center justify-center px-6 py-12">
      <div className="auth-blobs"><CardTintStreaks /></div>
      {/* Talisman wordmark */}
      <div className="mb-10">
        <TalismanLogo />
      </div>

      <div className="w-full max-w-sm">

        {/* ── Loading ── */}
        {stage === 'loading' && (
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-6 h-6 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
            <p className="text-sm text-app-sub font-medium">Verifying link…</p>
          </div>
        )}

        {/* ── Reset password form ── */}
        {stage === 'form' && mode === 'resetPassword' && (
          <>
            <h1 className="font-serif text-2xl font-bold text-app-text mb-1">New password</h1>
            {email && <p className="text-xs text-app-sub/60 font-medium mb-6">{email}</p>}
            <form onSubmit={handleReset} className="space-y-4">
              <div className="space-y-1.5">
                <label className="ds-label block pl-1">New password</label>
                <input
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  className={`${FIELD_AUTH} px-5 ${fieldBorder(false, true)}`}
                />
              </div>
              <div className="space-y-1.5">
                <label className="ds-label block pl-1">Confirm password</label>
                <input
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  className={`${FIELD_AUTH} px-5 ${fieldBorder(false, true)}`}
                />
              </div>
              {error && <p role="alert" className={AUTH_ERROR}>{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gold text-white rounded-2xl py-4 text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 mt-2"
              >
                {submitting ? 'Saving…' : 'Set new password'}
              </button>
            </form>
          </>
        )}

        {/* ── Success ── */}
        {stage === 'success' && (
          <div className="flex flex-col items-center text-center gap-4 py-2">
            <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center">
              <svg className="w-7 h-7 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h1 className="font-serif text-2xl font-bold text-app-text mb-1">
                {successMessages[mode]?.heading}
              </h1>
              <p className="text-sm text-app-sub/70 font-medium leading-relaxed">
                {successMessages[mode]?.body}
              </p>
            </div>
            <a
              href="https://talismantravel.app"
              className="mt-2 w-full bg-gold text-white rounded-2xl py-4 text-[11px] font-black uppercase tracking-widest shadow-lg shadow-gold/20 hover:brightness-110 active:scale-[0.98] transition text-center block"
            >
              Open Talisman
            </a>
          </div>
        )}

        {/* ── Error ── */}
        {stage === 'error' && (
          <div className="flex flex-col items-center text-center gap-4 py-2">
            <div role="alert">
              <h1 className="font-serif text-2xl font-bold text-app-text mb-3">Couldn’t complete this link</h1>
              <p className="text-base text-app-sub leading-relaxed">{error}</p>
            </div>
            <a
              href="https://talismantravel.app"
              className="mt-2 w-full bg-app-text text-white rounded-2xl py-4 text-[11px] font-black uppercase tracking-widest hover:brightness-110 active:scale-[0.98] transition text-center block"
            >
              Back to Talisman
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
